import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function InvoiceCreatePage() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');

  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [form, setForm] = useState({
    CustomerName: '', GSTIN: '', Address: '', Phone: '', Email: '',
    InvoiceDate: new Date().toISOString().slice(0, 10),
    DueDate: '', Notes: '', Discount: ''
  });

  const [items, setItems] = useState([
    { Description: '', HSN: '', Size: '', Quantity: '', Rate: '', GSTPercent: '0', CGST: '0', SGST: '0', Amount: '' },
  ]);

  // ---- Product Search Modal ----
  const [showProductModal, setShowProductModal] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productRowIdx, setProductRowIdx] = useState(null);

  const subTotal = items.reduce((sum, i) => sum + +(i.Amount || 0), 0);
  const discount = parseFloat(form.Discount || 0);
  const grandTotal = (subTotal - discount > 0 ? subTotal - discount : 0).toFixed(2);

  useEffect(() => {
    api.get('/customers').then(res => {
      setCustomers(res.data);
      setFilteredCustomers(res.data);
    }).catch(console.error);
  }, []);

  const handleCustomerInput = (e) => {
    const input = e.target.value;
    setForm({ ...form, CustomerName: input });
    setShowSuggestions(true);
    const matches = customers.filter(c => c.Name.toLowerCase().includes(input.toLowerCase()));
    setFilteredCustomers(matches);
  };

  const handlePickCustomer = (cust) => {
    setForm({
      ...form,
      CustomerName: cust.Name,
      GSTIN: cust.GSTIN || '',
      Address: cust.Address || '',
      Phone: cust.Phone || '',
      Email: cust.Email || ''
    });
    setSelectedCustomerId(cust.CustomerID);
    setShowSuggestions(false);
  };

  const handleChange = (e, idx, field) => {
    const updated = [...items];
    updated[idx][field] = e.target.value;
    const qty = parseFloat(updated[idx].Quantity) || 0;
    const rate = parseFloat(updated[idx].Rate) || 0;
    const gstPercent = parseFloat(updated[idx].GSTPercent) || 0;
    const baseAmount = qty * rate;
    const gstAmount = baseAmount * gstPercent / 100;
    updated[idx].CGST = (gstPercent / 2).toFixed(2);
    updated[idx].SGST = (gstPercent / 2).toFixed(2);
    updated[idx].Amount = (baseAmount + gstAmount).toFixed(2);
    setItems(updated);
  };

  const addItem = () => {
    setItems([...items, { Description: '', HSN: '', Size: '', Quantity: '', Rate: '', GSTPercent: '0', CGST: '0', SGST: '0', Amount: '' }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/invoices/create', {
        userId,
        CustomerID: selectedCustomerId,
        InvoiceDate: form.InvoiceDate,
        DueDate: form.DueDate,
        Notes: form.Notes,
        Discount: form.Discount || 0,
        Items: items.map(item => ({ ...item, GSTPercent: item.GSTPercent || '0', Size: item.Size || '' })),
      });
      toast.success('Invoice created successfully');
      setTimeout(() => navigate('/invoices'), 1000);
    } catch (err) {
      console.error(err);
      toast.error('Error saving invoice');
    }
  };

  // ---- Product Search Logic ----
  const handleOpenProductModal = (idx) => {
    setProductRowIdx(idx);
    setShowProductModal(true);
    setProductSearch('');
    setProductResults([]);
  };

  const fetchProductSearch = async (q) => {
    if (!q) {
      setProductResults([]);
      return;
    }
    try {
      const res = await api.get('/products', { params: { search: q } });
      setProductResults(res.data);
    } catch {
      setProductResults([]);
    }
  };

  // Styles for glassmorphism
  const cardStyle = {
    background: 'rgba(255,255,255,0.92)',
    borderRadius: '2rem',
    boxShadow: '0 8px 48px 0 rgba(40, 110, 250, 0.10)',
    backdropFilter: 'blur(9px)',
    border: '1.5px solid rgba(180,210,255,0.18)',
    margin: '0 auto',
    maxWidth: 1080
  };

  const stickyBarStyle = {
    background: 'rgba(255,255,255,0.98)',
    borderRadius: '2rem',
    boxShadow: '0 2px 16px 0 rgba(40, 110, 250, 0.09)',
    border: '1.5px solid #e1e9f7',
    position: 'sticky',
    bottom: 10,
    zIndex: 2,
    maxWidth: 980,
    margin: '0 auto',
  };

  return (
    <div
      className="min-vh-100 d-flex flex-column"
      style={{
        background: 'linear-gradient(120deg, #a7d1ff 0%, #eaf6ff 100%)',
        minHeight: '100vh',
        paddingBottom: 60,
      }}
    >
      <div className="container py-4 flex-grow-1 d-flex flex-column align-items-center" style={{ minHeight: '100vh' }}>
        <BackButton />
        <h2 className="mb-4 fw-bold text-primary text-center" style={{ letterSpacing: 1 }}>
          <span role="img" aria-label="invoice">🧾</span> Create Invoice
        </h2>
        <div className="card p-4 shadow-lg border-0 rounded-4 w-100 mb-4" style={cardStyle}>
          <form autoComplete="off" onSubmit={handleSubmit}>
            {/* Customer Details */}
            <div className="row g-3">
              <div className="col-md-6 position-relative">
                <label className="form-label fw-semibold">Customer Name</label>
                <input
                  type="text"
                  className="form-control rounded-pill px-3"
                  value={form.CustomerName}
                  onChange={handleCustomerInput}
                  placeholder="Type customer name"
                  required
                  autoFocus
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 120)}
                  onFocus={() => form.CustomerName && setShowSuggestions(true)}
                  autoComplete="off"
                />
                {(showSuggestions && filteredCustomers.length > 0 && form.CustomerName) && (
                  <ul className="list-group position-absolute w-100 z-3" style={{ top: '100%', maxHeight: 200, overflowY: 'auto' }}>
                    {filteredCustomers.slice(0, 5).map((cust, idx) => (
                      <li
                        key={idx}
                        className="list-group-item list-group-item-action"
                        style={{ cursor: 'pointer' }}
                        onMouseDown={() => handlePickCustomer(cust)}
                      >{cust.Name}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="col-md-3">
                <label className="form-label">Phone</label>
                <input className="form-control rounded-pill px-3" value={form.Phone} readOnly />
              </div>
              <div className="col-md-3">
                <label className="form-label">Email</label>
                <input className="form-control rounded-pill px-3" value={form.Email} readOnly />
              </div>
            </div>
            <div className="row g-3 mt-2">
              <div className="col-md-4 col-lg-3">
                <label className="form-label">GSTIN</label>
                <input className="form-control rounded-pill px-3" value={form.GSTIN} readOnly />
              </div>
              <div className="col-md-8 col-lg-9">
                <label className="form-label">Address</label>
                <input className="form-control rounded-pill px-3" value={form.Address} readOnly />
              </div>
            </div>
            <div className="row g-3 mt-3">
              <div className="col-md-4">
                <label className="form-label">Invoice Date</label>
                <input type="date" className="form-control rounded-pill px-3" value={form.InvoiceDate} onChange={e => setForm({ ...form, InvoiceDate: e.target.value })} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Due Date</label>
                <input type="date" className="form-control rounded-pill px-3" value={form.DueDate} onChange={e => setForm({ ...form, DueDate: e.target.value })} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Discount (₹)</label>
                <input type="number" className="form-control rounded-pill px-3" placeholder="Discount Amount" value={form.Discount || ''} onChange={e => setForm({ ...form, Discount: e.target.value })} />
              </div>
            </div>
            <label className="form-label mt-3">Notes</label>
            <textarea className="form-control rounded-4" placeholder="Notes" value={form.Notes} onChange={e => setForm({ ...form, Notes: e.target.value })}></textarea>
            {/* Items Table */}
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="fw-semibold mb-0">Invoice Items</h5>
                <button type="button" className="btn btn-outline-primary btn-sm px-3 rounded-pill" onClick={addItem}>+ Add Item</button>
              </div>
              <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle" style={{ fontSize: 15 }}>
                  <thead className="table-light">
                    <tr>
                      <th>Description</th>
                      <th>HSN</th>
                      <th>Size</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>GST %</th>
                      <th>CGST %</th>
                      <th>SGST %</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <input className="form-control" placeholder="Description" value={item.Description} onChange={e => handleChange(e, idx, 'Description')} required />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              title="Search Product"
                              onClick={() => handleOpenProductModal(idx)}
                              tabIndex={-1}
                            >
                              <span role="img" aria-label="search">🔍</span>
                            </button>
                          </div>
                        </td>
                        <td><input className="form-control" placeholder="HSN" value={item.HSN} onChange={e => handleChange(e, idx, 'HSN')} /></td>
                        <td><input className="form-control" placeholder="Size" value={item.Size} onChange={e => handleChange(e, idx, 'Size')} /></td>
                        <td><input className="form-control" type="number" placeholder="Qty" value={item.Quantity} onChange={e => handleChange(e, idx, 'Quantity')} /></td>
                        <td><input className="form-control" type="number" placeholder="Rate" value={item.Rate} onChange={e => handleChange(e, idx, 'Rate')} /></td>
                        <td><input className="form-control" type="number" placeholder="GST %" value={item.GSTPercent} onChange={e => handleChange(e, idx, 'GSTPercent')} /></td>
                        <td><input className="form-control" placeholder="CGST %" value={item.CGST} readOnly tabIndex={-1} /></td>
                        <td><input className="form-control" placeholder="SGST %" value={item.SGST} readOnly tabIndex={-1} /></td>
                        <td><input className="form-control" placeholder="Amount" value={item.Amount} readOnly tabIndex={-1} /></td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm rounded-pill"
                            onClick={() => removeItem(idx)}
                            disabled={items.length === 1}
                            title="Remove item"
                          >✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Sticky Summary Bar */}
            <div
              className="shadow rounded-4 p-3 mt-4 bg-white d-flex flex-wrap justify-content-between align-items-center"
              style={stickyBarStyle}
            >
              <span className="fw-bold" style={{ fontSize: 18 }}>Subtotal: <span className="text-primary">₹ {subTotal.toFixed(2)}</span></span>
              <span className="fw-bold" style={{ fontSize: 18 }}>Discount: <span className="text-primary">₹ {discount.toFixed(2)}</span></span>
              <span className="fw-bold" style={{ fontSize: 20 }}>Grand Total: <span className="text-success">₹ {grandTotal}</span></span>
            </div>
            <button
              type="submit"
              className="btn btn-primary w-100 py-3 rounded-pill fw-bold fs-5 mt-3"
            >Save Invoice</button>
          </form>
        </div>
      </div>

      {/* -------- Product Search Modal -------- */}
      {showProductModal && (
        <div
          className="modal fade show"
          style={{
            display: 'block',
            background: 'rgba(0,0,0,0.18)',
            zIndex: 2000,
          }}
          tabIndex={-1}
          onClick={() => setShowProductModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: 480 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-content shadow-lg rounded-3">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Search Product</h5>
                <button type="button" className="btn-close" onClick={() => setShowProductModal(false)} />
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Type product name or HSN"
                  value={productSearch}
                  onChange={e => {
                    setProductSearch(e.target.value);
                    fetchProductSearch(e.target.value);
                  }}
                  autoFocus
                />
                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                  {productResults.length === 0 && productSearch && (
                    <div className="text-muted text-center py-2">No products found.</div>
                  )}
                  {productResults.map(prod => (
                    <button
                      key={prod.ProductID}
                      type="button"
                      className="list-group-item list-group-item-action mb-1"
                      style={{
                        borderRadius: 8,
                        border: '1px solid #eef1f8',
                        background: '#f8fbff',
                        display: 'block',
                        width: '100%',
                        textAlign: 'left'
                      }}
                      onClick={() => {
                        // Update the item row
                        const updated = [...items];
                        updated[productRowIdx] = {
                          ...updated[productRowIdx],
                          Description: prod.Name,
                          HSN: prod.HSN,
                          Rate: prod.Rate,
                          GSTPercent: prod.GSTPercent,
                        };
                        // recalc
                        const qty = parseFloat(updated[productRowIdx].Quantity) || 0;
                        const rate = parseFloat(prod.Rate) || 0;
                        const gstPercent = parseFloat(prod.GSTPercent) || 0;
                        const baseAmount = qty * rate;
                        const gstAmount = baseAmount * gstPercent / 100;
                        updated[productRowIdx].CGST = (gstPercent / 2).toFixed(2);
                        updated[productRowIdx].SGST = (gstPercent / 2).toFixed(2);
                        updated[productRowIdx].Amount = (baseAmount + gstAmount).toFixed(2);

                        setItems(updated);
                        setShowProductModal(false);
                      }}
                    >
                      <div className="fw-bold">{prod.Name}</div>
                      <div style={{ fontSize: 13 }}>
                        <span>HSN: {prod.HSN} &nbsp;&nbsp; Rate: ₹{prod.Rate} &nbsp;&nbsp; GST: {prod.GSTPercent}%</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
